'use client'

import { Button, Radio, Stack, Text, TextInput } from '@mantine/core'
import { post } from '@partment/client'
import { FC, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import ReCaptchaProvider, { RECAPTCHA_SITE_KEY } from '../../Providers/ReCaptchaProvider'
import {
  HONEYPOT_FIELD,
  LeadFormHoneypot,
  TOO_MANY_SUBMISSIONS_MESSAGE,
  useRecaptchaToken,
} from '../LeadFormProtection'

// Values are stored verbatim on WebsiteLead.interested_in / heard_about_us
const INTERESTED_IN = ['Investment Only', 'Co-ownership']
const HEARD_ABOUT_US = ['Social Media', 'Offline Event', 'Referral', 'Other']

type FormValues = {
  first_name: string
  last_name: string
  phone: string
  email: string
  interested_in: string
  heard_about_us: string
  heard_about_us_other: string
  [HONEYPOT_FIELD]: string
}

type LeadResponse = { code?: number; message?: { message?: string }; statusCode?: number; error?: string }

type ScheduleCallFormProps = { onSubmitted?: () => void }

// Every production build has the site key (see ReCaptchaProvider). Without one
// (local dev) there is no provider; the form still works and the API decides
// (it doesn't require a token yet).
// Apps bake different API versions into @partment/client's baseURL (v2, v3 or
// none), so build an absolute URL from the base every app shares.
const websiteLeadUrl = () =>
  `${(process.env.NEXT_PUBLIC_API_BASE_URL || '').replace(/\/+$/, '')}/v2/website-lead/`

/**
 * "Schedule a call" request form — replaces the Zoho Forms embed
 * (zfrmz.com/bv5Q2CpVHQ6TFvZHiVpX). Posts to the Django WebsiteLead API as
 * lead_type "schedule a call"; sales gets the alert email.
 */
export const ScheduleCallForm: FC<ScheduleCallFormProps> = (props) =>
  RECAPTCHA_SITE_KEY ? (
    <ReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
      <ScheduleCallFormBody {...props} />
    </ReCaptchaProvider>
  ) : (
    <ScheduleCallFormBody {...props} />
  )

const ScheduleCallFormBody: FC<ScheduleCallFormProps> = ({ onSubmitted }) => {
  const getRecaptchaToken = useRecaptchaToken()
  const [submitted, setSubmitted] = useState(false)
  const [serverError, setServerError] = useState('')

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    mode: 'onBlur',
    defaultValues: {
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      interested_in: '',
      heard_about_us: '',
      heard_about_us_other: '',
      [HONEYPOT_FIELD]: '',
    },
  })

  const heardAboutUs = watch('heard_about_us')

  const onSubmit = async (values: FormValues) => {
    setServerError('')
    const heard =
      values.heard_about_us === 'Other' && values.heard_about_us_other.trim()
        ? `Other: ${values.heard_about_us_other.trim()}`
        : values.heard_about_us
    const recaptchaToken = await getRecaptchaToken('schedule_call')

    const res = (await post(
      websiteLeadUrl(),
      {
        lead_type: 'schedule a call',
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        interested_in: values.interested_in,
        heard_about_us: heard,
        lead_source: 'website',
        source_url: typeof window !== 'undefined' ? window.location.href : '',
        is_subscribed: 0,
        [HONEYPOT_FIELD]: values[HONEYPOT_FIELD],
        ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
      },
      undefined,
      true,
    )) as LeadResponse

    if (res?.code === 200) {
      setSubmitted(true)
      onSubmitted?.()
      return
    }
    console.error('Schedule-a-call submission failed:', res)
    setServerError(
      res?.statusCode === 429
        ? TOO_MANY_SUBMISSIONS_MESSAGE
        : 'Something went wrong. Please try again or reach us on WhatsApp.',
    )
  }

  if (submitted) {
    return (
      <Stack align='center' gap='xs' py='xl'>
        <Text fw={700} fz='lg' c='#081433'>
          Thank you!
        </Text>
        <Text ta='center' c='#5C6166'>
          We&apos;ve received your request. A member of our team will call you within 1–2 business days.
        </Text>
      </Stack>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <LeadFormHoneypot {...register(HONEYPOT_FIELD)} />
      <Stack gap='md'>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
          <TextInput
            label='First name'
            withAsterisk
            error={errors.first_name?.message}
            {...register('first_name', { required: 'First name is required', maxLength: { value: 80, message: 'Too long' } })}
          />
          <TextInput
            label='Last name'
            withAsterisk
            error={errors.last_name?.message}
            {...register('last_name', { required: 'Last name is required', maxLength: { value: 80, message: 'Too long' } })}
          />
        </div>

        <TextInput
          label='Phone'
          type='tel'
          placeholder='+20 10 0000 0000'
          withAsterisk
          error={errors.phone?.message}
          {...register('phone', {
            required: 'Phone number is required',
            pattern: { value: /^\+?[\d\s()-]{7,20}$/, message: 'Enter a valid phone number' },
          })}
        />

        <TextInput
          label='Email'
          type='email'
          withAsterisk
          error={errors.email?.message}
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/, message: 'Enter a valid email address' },
          })}
        />

        <Controller
          name='interested_in'
          control={control}
          rules={{ required: 'Please choose one' }}
          render={({ field }) => (
            <Radio.Group label="I'm interested in" withAsterisk value={field.value} onChange={field.onChange} error={errors.interested_in?.message}>
              <Stack gap='xs' mt={6}>
                {INTERESTED_IN.map((opt) => (
                  <Radio key={opt} value={opt} label={opt} />
                ))}
              </Stack>
            </Radio.Group>
          )}
        />

        <Controller
          name='heard_about_us'
          control={control}
          rules={{ required: 'Please choose one' }}
          render={({ field }) => (
            <Radio.Group label='How did you hear about us?' withAsterisk value={field.value} onChange={field.onChange} error={errors.heard_about_us?.message}>
              <Stack gap='xs' mt={6}>
                {HEARD_ABOUT_US.map((opt) => (
                  <Radio key={opt} value={opt} label={opt} />
                ))}
              </Stack>
            </Radio.Group>
          )}
        />

        {heardAboutUs === 'Other' && (
          <TextInput label='Please tell us where' {...register('heard_about_us_other', { maxLength: { value: 100, message: 'Too long' } })} />
        )}

        {serverError && (
          <Text c='red' fz='sm' ta='center'>
            {serverError}
          </Text>
        )}

        <Button type='submit' loading={isSubmitting} radius='xl' size='md' color='#081433' fullWidth>
          Submit
        </Button>
      </Stack>
    </form>
  )
}
